/**
 * Stream / transcoder work-state → Bootstrap text-* classes.
 * Colors: accent/success (.text-success), accent/error (.text-danger), warning (.text-warning), muted (.text-muted).
 * See _new-admin-styles.scss (Nora tokens).
 */

/**
 * Maps API `streamState` (content-delivery streams) → utility class.
 * Pairs with `streamStatus`: ENABLED / DISABLED → streamDeliveryStatusCssClass().
 *
 * Example: streamStatus ENABLED + streamState TRANSITION → Stream column success, Status column warning.
 *          streamStatus ENABLED + streamState PROCESSING → Stream success, Status success (Transcoding).
 */
export const STREAM_WORK_STATE_BOOTSTRAP_CLASS = Object.freeze({
  TRANSCODING: 'text-success',
  PROCESSING: 'text-success',
  TRANSITION: 'text-warning',
  DONE: 'text-success',
  ERROR: 'text-danger',
  NOT_AVAILABLE: 'text-danger',
  WAITING: 'text-warning'
})

/** Stream delivery streamStatus: Enabled → accent/success, Disabled → accent/error */
export const STREAM_DELIVERY_STATUS_BOOTSTRAP_CLASS = Object.freeze({
  ENABLED: 'text-success',
  DISABLED: 'text-danger',
  SCHEDULED: 'text-warning'
})

/**
 * @param {string|null|undefined} streamState - e.g. PROCESSING, ERROR
 * @param {{ active?: boolean }} [meta] - transcoder payloads: when active===false → muted
 * @returns {string}
 */
export function streamWorkStateCssClass (streamState, meta = {}) {
  let state = (streamState || '').toString().toUpperCase().trim()
  if (state === 'FAILED') state = 'ERROR'

  if (STREAM_WORK_STATE_BOOTSTRAP_CLASS[state]) {
    return STREAM_WORK_STATE_BOOTSTRAP_CLASS[state]
  }
  const active = meta.active
  if (active === false) return 'text-muted'
  return 'text-success'
}

/**
 * @param {Object|null|undefined} stream - item from /streams list or similar
 * @returns {string}
 */
export function streamWorkStateCssClassFromStream (stream) {
  if (!stream) return 'text-muted'
  const state =
    stream.status?.statusState ??
    stream.statusState ??
    stream.streamState ??
    stream.workStatus ??
    ''
  const active = stream.status?.active ?? stream.active
  return streamWorkStateCssClass(state, { active })
}

/**
 * @param {string|null|undefined} streamStatus - ENABLED, DISABLED, SCHEDULED
 * @returns {string}
 */
export function streamDeliveryStatusCssClass (streamStatus) {
  const s = (streamStatus || '').toString().toUpperCase().trim()
  if (STREAM_DELIVERY_STATUS_BOOTSTRAP_CLASS[s]) {
    return STREAM_DELIVERY_STATUS_BOOTSTRAP_CLASS[s]
  }
  return 'text-muted'
}

/**
 * Setrix `statusState` (e.g. from status.statusState) → Bootstrap text-* class for transcoding Status column.
 * PROCESSING → text-success; ERROR → text-danger; TRANSITION and all other values → text-muted.
 *
 * @param {string|null|undefined} statusState
 * @returns {string}
 */
export function transcodingStatusStateCssClass (statusState) {
  let s = (statusState || '').toString().toUpperCase().trim()
  if (s === 'FAILED') s = 'ERROR'
  if (s === 'PROCESSING') return 'text-success'
  if (s === 'ERROR') return 'text-danger'
  return 'text-muted'
}

/**
 * Visible status text: API `statusLabel` only; otherwise `emptyDisplay` (e.g. em dash or placeholder).
 *
 * @param {string|undefined} statusLabel - API status.statusLabel
 * @returns {string}
 */
export function transcodingStatusDisplayText (statusLabel) {
  if (statusLabel != null && String(statusLabel).trim() !== '') {
    return String(statusLabel).trim()
  }
}
